'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { Chip } from "@/components/ui/chip";

export interface ChipSelectOption {
 label: string
 value: string
}

interface ChipSelectProps {
 value: string | undefined
 onValueChange: (value: string) => void
 options: readonly ChipSelectOption[]
 className?: string
 'aria-label'?: string
}

function keyboardFocusTarget(key: string, currentIndex: number, count: number): number | null {
 switch (key) {
  case 'ArrowRight':
  case 'ArrowDown':
   return currentIndex < count - 1 ? currentIndex + 1 : 0
  case 'ArrowLeft':
  case 'ArrowUp':
   return currentIndex > 0 ? currentIndex - 1 : count - 1
  case 'Home':
   return 0
  case 'End':
   return count - 1
  default:
   return null
 }
}

function useChipKeyboard(
 options: readonly ChipSelectOption[],
 value: string | undefined,
 onValueChange: (value: string) => void,
) {
 const [focusedIndex, setFocusedIndex] = React.useState<number>(-1)

 const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key === 'Enter' || e.key === ' ') {
   e.preventDefault()
   if (focusedIndex >= 0 && focusedIndex < options.length) {
    onValueChange(options[focusedIndex].value)
   }
   return
  }
  const currentIndex = focusedIndex === -1 ? options.findIndex((opt) => opt.value === value) : focusedIndex
  const targetIndex = keyboardFocusTarget(e.key, currentIndex, options.length)
  if (targetIndex === null) return
  e.preventDefault()
  setFocusedIndex(targetIndex)
 }

 return { focusedIndex, setFocusedIndex, handleKeyDown }
}

export function ChipSelect({
 value,
 onValueChange,
 options,
 className,
 'aria-label': ariaLabel,
}: ChipSelectProps) {
 const containerRef = React.useRef<HTMLDivElement>(null)
 const { focusedIndex, setFocusedIndex, handleKeyDown } = useChipKeyboard(options, value, onValueChange)

 return (
  <div
   ref={containerRef}
   role="radiogroup"
   aria-label={ariaLabel}
   tabIndex={0}
   onKeyDown={handleKeyDown}
   onBlur={() => setFocusedIndex(-1)}
   className={cn('flex flex-wrap gap-2', className)}
  >
   {options.map((option, index) => {
    const isSelected = option.value === value
    const isFocused = index === focusedIndex

    return (
     <Chip
      key={option.value}
      asChild
      tone={isSelected ? 'selected' : 'outline'}
      focused={isFocused}
     >
      <button
       type="button"
       role="radio"
       aria-checked={isSelected}
       tabIndex={-1}
       onClick={() => onValueChange(option.value)}
      >
       {option.label}
      </button>
     </Chip>
    )
   })}
  </div>
 )
}
